import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { propertyTypeSummary } from "@rentorng/core";
import { PropertyTypeMarks } from "@/components/property-icons";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { titleCase } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useLimit, useRecordLock } from "@/lib/gating";
import { Padlock, PadlockButton, Refusal } from "@/components/padlock";
import { Badge, Button, Card, EmptyState, ErrorState, Input, LinkButton, PageHeader, Spinner, Table, Tabs } from "@/components/ui";
import { PinButton } from "@/components/pin-button";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "@/lib/mutations";

interface Row {
  id: string;
  name: string;
  types: string[];
  city: string | null;
  state: string | null;
  addressLine: string | null;
  landlordName: string | null;
  landlordId: string | null;
  /** Distinct owners across the units — more than one means the block was sold off in parts. */
  ownerCount: number;
  unitCount: number;
  occupiedCount: number;
  vacantCount: number;
  /** When it was pinned to the top of the list, or null. */
  pinnedAt?: string | null;
}

/** A number that opens the list behind it. A zero has nothing to show, so it stays plain. */
function CountLink({ to, count }: { to: string; count: number }) {
  if (!count) return <span className="tabular text-ink-400">0</span>;
  return (
    <Link to={to} className="tabular font-medium text-ink-800 underline-offset-2 hover:text-brand-700 hover:underline">
      {count}
    </Link>
  );
}

export function PropertiesPage() {
  const { vocab, can } = useSession();
  const list = useListParams();
  const queryClient = useQueryClient();

  /*
    What the plan padlocks, decided by the server and read here — never re-derived.

    Two separate questions, and they are answered by two separate things. `lock` is about the
    records that already exist: over the allowance, the newest stay open and the older ones carry a
    padlock, and **every one of them is still drawn, in the same list, in the same order**. Nothing
    is filtered out, archived or hidden; the money on a padlocked building goes on moving exactly as
    it did. `ceiling` is about the next one: at the limit, the way to add another is drawn shut
    rather than removed.

    Both are already permission-aware — `useRecordLock` returns nothing at all to somebody without
    edit rights, because a padlock they could never open with money is noise on their screen. See
    the note on that function.
  */
  const lock = useRecordLock("properties");
  const ceiling = useLimit("properties");
  const addGate = can("property.edit") ? (ceiling?.gate ?? null) : null;

  /*
    Archived is a tab in the address, not a switch on the page.

    A list that shows both would be a list where somebody signs a tenancy against a building the
    agency has finished with. Two addresses, two lists, and the one you are on is in the URL — so it
    can be linked to, reloaded and gone back from like anything else here.
  */
  const [params] = useSearchParams();
  const archived = params.get("archived") === "1";

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["properties", list.q, list.page, archived],
    queryFn: () =>
      api.get<{ properties: Row[]; page: PageMeta }>("/properties", {
        q: list.q,
        page: list.page,
        ...(archived ? { archived: "1" } : {}),
      }),
    // The previous page stays on screen while the next one loads, so the table does not blink
    // back to a spinner on every keystroke and every page change.
    placeholderData: (previous) => previous,
  });

  const rows = data?.properties ?? [];

  /**
   * Out of every list, or back into them.
   *
   * The state is sent rather than toggled — two people on two screens pressing archive should agree
   * rather than flip it back and forth — and everything that counts properties is refreshed after,
   * because a building leaving the books changes more than this table.
   */
  const archive = useApiMutation({
    mutationFn: (input: { id: string; archived: boolean }) =>
      api.post(`/properties/${input.id}/archive`, { archived: input.archived }),
    invalidates: [["properties"], ["dashboard"], ["units"]],
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["properties"] }),
  });

  return (
    <div>
      <PageHeader
        title={titleCase(vocab.properties)}
        subtitle={`${data?.page?.total ?? rows.length} ${(data?.page?.total ?? rows.length) === 1 ? vocab.property : vocab.properties}`}
        description={`Every building or site you manage. Open one to see its ${vocab.units}, who owns them and what is empty — or add a ${vocab.property} and its ${vocab.units} in one go.`}
        actions={
          <>
            <ExportButton kind="properties" />
            {addGate ? (
              <PadlockButton gate={addGate}>Add a {vocab.property}</PadlockButton>
            ) : can("property.edit") ? (
              <LinkButton to="/app/properties/new" need="property.edit">Add a {vocab.property}</LinkButton>
            ) : null}
          </>
        }
      />

      <Tabs
        current={archived ? "/app/properties?archived=1" : "/app/properties"}
        tabs={[
          { to: "/app/properties", label: `On the books` },
          { to: "/app/properties?archived=1", label: "Archived" },
        ]}
      />

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder={`Search ${vocab.properties} by name or area…`} />
        </div>

        {/* A refusal that names a plan is drawn as the padlock and the way out of it, not as a red
            box saying no. Every other kind of failure is the red box it always was — `Refusal`
            makes that choice once so no screen can end up drawing both. */}
        {archive.error ? (
          <div className="p-3">
            <Refusal error={archive.error} message={archive.errorMessage} />
          </div>
        ) : null}

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={list.q ? `No ${vocab.properties} matched “${list.q}”.` : `No ${vocab.properties} yet.`}
            hint={`Add the building first, then its ${vocab.units}. A single house is still a ${vocab.property} with one ${vocab.unit} — that keeps every report consistent.`}
            action={
              addGate ? (
                <PadlockButton gate={addGate}>Add a {vocab.property}</PadlockButton>
              ) : can("property.edit") ? (
                <LinkButton to="/app/properties/new" need="property.edit">Add a {vocab.property}</LinkButton>
              ) : undefined
            }
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Where</th>
                <th>{titleCase(vocab.landlord)}</th>
                <th className="num">{titleCase(vocab.units)}</th>
                <th className="num">Occupied</th>
                <th className="num">Vacant</th>
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {/*
                    The mark is the anchor for the row — shape first, then the name, then the pin.

                    The pin is last rather than first because it is a control and not an identity:
                    a column of pins down the left would be the first thing read on a screen where
                    the answer is which building this is.
                  */}
                  <span className="flex items-center gap-2.5">
                    <PropertyTypeMarks types={row.types} size={17} />
                    <Link to={`/app/properties/${row.id}`} className="link">{row.name}</Link>
                    {/*
                      The padlock, beside the name it belongs to.

                      **The row is untouched otherwise** — it opens, it shows its counts, its
                      tallies still link through to the units behind them. What the padlock marks
                      is that this building is above what the plan opens, so the things somebody
                      would do *to* it are shut; pressing the mark says so and offers the fix.
                    */}
                    {lock.isLocked(row.id) ? <Padlock gate={lock.gate} label="Locked" /> : null}
                    {archived ? null : (
                      <PinButton kind="property" id={row.id} pinnedAt={row.pinnedAt} invalidates={[["properties"]]} />
                    )}
                    {/*
                      Archiving stays live on a padlocked row, and this is deliberate.

                      ADR-0106 refuses editing, deleting, publishing, letting and extending a
                      padlocked record, and deliberately refuses none of archiving: an archived
                      building is counted by neither the ceiling nor the padlock, so archiving is
                      the one honest route back under a free allowance, and it costs nobody
                      anything. A padlock here would be a wall dressed as a padlock — the way out
                      shut by the thing it is the way out of. Restoring is open for the mirror
                      reason: a building that could be archived and never brought back is a
                      building we took.
                    */}
                    {can("property.edit") ? (
                      <button
                        type="button"
                        className="ml-auto shrink-0 rounded-[var(--radius-control)] px-2 py-1 text-xs font-semibold text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800"
                        disabled={archive.isPending}
                        onClick={() => {
                          if (archived) {
                            archive.mutate({ id: row.id, archived: false });
                            return;
                          }
                          if (window.confirm(`Archive ${row.name}? It leaves every list until you restore it.`)) {
                            archive.mutate({ id: row.id, archived: true });
                          }
                        }}
                      >
                        {archived ? "Restore" : "Archive"}
                      </button>
                    ) : null}
                  </span>
                </td>
                <td className="text-ink-500">{propertyTypeSummary(row.types)}</td>
                <td className="text-ink-500">{[row.city, row.state].filter(Boolean).join(", ") || row.addressLine || "—"}</td>
                <td>
                  {row.landlordId ? (
                    <Link to={`/app/clients/${row.landlordId}`} className="link">{row.landlordName}</Link>
                  ) : (
                    <span className="text-ink-400">Not set</span>
                  )}
                  {row.ownerCount > 1 ? (
                    <span className="block text-xs text-ink-500">
                      +{row.ownerCount - 1} other {row.ownerCount === 2 ? vocab.landlord : `${vocab.landlord}s`}
                    </span>
                  ) : null}
                </td>
                {/* Each tally opens the units behind it, filtered to this building. */}
                <td className="num tabular">
                  <CountLink to={`/app/units?propertyId=${row.id}`} count={row.unitCount} />
                </td>
                <td className="num tabular">
                  <CountLink to={`/app/units?propertyId=${row.id}&occupancy=occupied`} count={row.occupiedCount} />
                </td>
                <td className="num">
                  {row.vacantCount > 0 ? (
                    <Link to={`/app/units?propertyId=${row.id}&status=vacant`}>
                      <Badge tone="danger">{row.vacantCount}</Badge>
                    </Link>
                  ) : (
                    <span className="tabular text-ink-400">0</span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={vocab.properties} />
      </Card>
    </div>
  );
}
